if (!customElements.get('subscription-product-card')) {
  const LOOP_APP_ID = '5284869';

  function formatMoney(cents, formatString) {
    if (cents == null || isNaN(cents)) return '';
    const amount = Math.round(Number(cents));
    const format = formatString || '{{amount}}';
    const placeholderRegex = /\{\{\s*(\w+)\s*\}\}/;

    const formatWithDelimiters = (number, precision = 2, thousands = ',', decimal = '.') => {
      const fixed = (number / 100).toFixed(precision);
      const parts = fixed.split('.');
      const dollars = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, `$1${thousands}`);
      const centsPart = parts[1] ? decimal + parts[1] : '';
      return dollars + centsPart;
    };

    const match = format.match(placeholderRegex);
    if (!match) return formatWithDelimiters(amount, 0);

    let value = '';
    switch (match[1]) {
      case 'amount':
        value = formatWithDelimiters(amount, 2);
        break;
      case 'amount_no_decimals':
        value = formatWithDelimiters(amount, 0);
        break;
      case 'amount_with_comma_separator':
        value = formatWithDelimiters(amount, 2, '.', ',');
        break;
      case 'amount_no_decimals_with_comma_separator':
        value = formatWithDelimiters(amount, 0, '.', ',');
        break;
      case 'amount_with_apostrophe_separator':
        value = formatWithDelimiters(amount, 2, "'", '.');
        break;
      case 'amount_no_decimals_with_space_separator':
        value = formatWithDelimiters(amount, 0, ' ', '.');
        break;
      case 'amount_with_space_separator':
        value = formatWithDelimiters(amount, 2, ' ', ',');
        break;
      case 'amount_with_period_and_space_separator':
        value = formatWithDelimiters(amount, 2, ' ', '.');
        break;
      default:
        value = formatWithDelimiters(amount, 0);
    }

    return format.replace(placeholderRegex, value);
  }

  class SubscriptionProductCard extends HTMLElement {
    connectedCallback() {
      if (this._initialized) return;
      this._initialized = true;

      this.moneyFormat =
        window.subscriptionMoneyFormat || this.dataset.moneyFormat || '{{amount}}';
      this.addToCartLabel = this.dataset.addToCartLabel || 'Add to cart';
      this.soldOutLabel = this.dataset.soldOutLabel || 'Sold out';
      this.hasSubscription = this.dataset.hasSubscription === 'true';

      const jsonEl = this.querySelector('[data-subscription-product-json]');
      if (!jsonEl) return;

      try {
        this.product = JSON.parse(jsonEl.textContent);
      } catch (e) {
        return;
      }

      this.loopGroup = (this.product.selling_plan_groups || []).find((g) => String(g.app_id) === LOOP_APP_ID);
      this.variantInput = this.querySelector('.subscription-product-card__variant-input');
      this.sellingPlanInput = this.querySelector('.subscription-product-card__selling-plan-input');
      this.variantSelect = this.querySelector('.subscription-product-card__variant-select');
      this.sellingPlanSelect = this.querySelector('.subscription-product-card__selling-plan-select');
      this.frequencyField = this.querySelector('[data-frequency-field]');
      this.submitButton = this.querySelector('[data-subscription-submit]');
      this.buttonText = this.querySelector('[data-button-text]');
      this.purchaseRadios = this.querySelectorAll('[data-purchase-type]');
      this.purchaseOptions = this.querySelectorAll('.subscription-product-card__purchase-option');
      this.onetimePriceEl = this.querySelector('[data-price-type="onetime"]');

      this.purchaseRadios.forEach((radio) => {
        radio.addEventListener('change', () => this.onPurchaseTypeChange());
      });

      if (this.variantSelect) {
        this.variantSelect.addEventListener('change', () => this.onVariantChange());
      }

      if (this.sellingPlanSelect) {
        this.sellingPlanSelect.addEventListener('change', () => this.onSellingPlanChange());
      }

      this.syncPurchaseOptionStyles();
      const variant = this.getVariant();
      if (variant) this.rebuildSellingPlanOptions(variant);
      this.updateAllPrices();
    }

    getSelectedPurchaseType() {
      const checked = this.querySelector('[data-purchase-type]:checked');
      return checked ? checked.dataset.purchaseType : 'subscription';
    }

    getSelectedVariantId() {
      if (this.variantSelect) return Number(this.variantSelect.value);
      if (this.variantInput) return Number(this.variantInput.value);
      return this.product.variants[0]?.id;
    }

    getVariant() {
      const id = this.getSelectedVariantId();
      return (this.product.variants || []).find((v) => v.id === id);
    }

    getSelectedSellingPlanId() {
      if (!this.hasSubscription || this.getSelectedPurchaseType() !== 'subscription') return null;
      if (this.sellingPlanSelect) return Number(this.sellingPlanSelect.value);
      return this.loopGroup?.selling_plans?.[0]?.id || null;
    }

    getAllocationPrice(variant, sellingPlanId) {
      if (!variant || !sellingPlanId) return null;
      const allocation = (variant.selling_plan_allocations || []).find(
        (a) => Number(a.selling_plan_id) === Number(sellingPlanId)
      );
      if (allocation) return allocation.price;
      return Math.round(variant.price * 0.9);
    }

    onPurchaseTypeChange() {
      this.syncPurchaseOptionStyles();

      const isSubscription = this.getSelectedPurchaseType() === 'subscription';

      if (this.frequencyField) {
        this.frequencyField.hidden = !isSubscription;
      }

      if (this.sellingPlanInput) {
        if (isSubscription) {
          this.sellingPlanInput.setAttribute('name', 'selling_plan');
          this.sellingPlanInput.value = this.getSelectedSellingPlanId() || '';
        } else {
          this.sellingPlanInput.removeAttribute('name');
          this.sellingPlanInput.value = '';
        }
      }

      this.updateAllPrices();
    }

    onVariantChange() {
      const variant = this.getVariant();
      if (!variant || !this.variantInput) return;

      this.variantInput.value = String(variant.id);
      this.variantInput.toggleAttribute('disabled', !variant.available);

      if (this.submitButton) {
        this.submitButton.disabled = !variant.available;
      }

      this.rebuildSellingPlanOptions(variant);
      this.updateAllPrices();
    }

    onSellingPlanChange() {
      if (this.sellingPlanInput && this.getSelectedPurchaseType() === 'subscription') {
        this.sellingPlanInput.value = this.getSelectedSellingPlanId() || '';
      }
      this.updateAllPrices();
    }

    rebuildSellingPlanOptions(variant) {
      if (!this.sellingPlanSelect || !this.loopGroup) return;

      const previousId = this.getSelectedSellingPlanId();
      const groupId = String(this.loopGroup.id);
      const seenLabels = new Set();
      const plans = [];

      (variant.selling_plan_allocations || []).forEach((allocation) => {
        if (String(allocation.selling_plan_group_id) !== groupId) return;

        const sp = (this.loopGroup.selling_plans || []).find(
          (plan) => Number(plan.id) === Number(allocation.selling_plan_id)
        );
        if (!sp) return;

        const label = sp.options?.[0]?.value || sp.name;
        if (seenLabels.has(label)) return;

        seenLabels.add(label);
        plans.push({ id: sp.id, label });
      });

      this.sellingPlanSelect.innerHTML = '';

      plans.forEach(({ id, label }) => {
        const option = document.createElement('option');
        option.value = id;
        option.textContent = label;
        this.sellingPlanSelect.appendChild(option);
      });

      const options = Array.from(this.sellingPlanSelect.options);
      const recommended = options.find((opt) => opt.textContent.includes('(Recommended)'));
      const restore = options.find((opt) => Number(opt.value) === previousId);
      const selected = recommended || restore || options[0];

      if (selected) {
        this.sellingPlanSelect.value = selected.value;
      }

      if (this.sellingPlanInput && this.getSelectedPurchaseType() === 'subscription') {
        this.sellingPlanInput.value = this.sellingPlanSelect.value;
      }
    }

    syncPurchaseOptionStyles() {
      this.purchaseOptions.forEach((option) => {
        const radio = option.querySelector('[data-purchase-type]');
        option.classList.toggle(
          'subscription-product-card__purchase-option--selected',
          radio && radio.checked
        );
      });
    }

    updateAllPrices() {
      const variant = this.getVariant();
      if (!variant) return;

      const onetimePrice = variant.price;
      const sellingPlanId = this.getSelectedSellingPlanId();
      const subscriptionPrice = this.hasSubscription
        ? this.getAllocationPrice(variant, sellingPlanId) ?? Math.round(onetimePrice * 0.9)
        : onetimePrice;

      if (this.onetimePriceEl) {
        this.onetimePriceEl.textContent = formatMoney(onetimePrice, this.moneyFormat);
      }

      const activePrice =
        this.hasSubscription && this.getSelectedPurchaseType() === 'subscription'
          ? subscriptionPrice
          : onetimePrice;

      if (this.buttonText) {
        if (!variant.available) {
          this.buttonText.textContent = this.soldOutLabel;
        } else {
          this.buttonText.textContent = `${this.addToCartLabel} - ${formatMoney(activePrice, this.moneyFormat)}`;
        }
      }
    }
  }

  customElements.define('subscription-product-card', SubscriptionProductCard);
}
